/**
 * Policy Edit Form Component
 * Structured form for editing policy draft with visual attribute and rule editors
 */

import { useState, useMemo, useCallback } from 'react'
import { ChevronDown, ChevronRight, Code, Save, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import type { PolicyDraft, UpdatePolicyRequest, ParsedXacmlPolicy, ParsedXacmlAttributes } from '@/types/policy'
import { parseXacmlPolicy } from '@/lib/xacml-parser'
import { serializeToXacml, createEmptyPolicy } from '@/lib/xacml-serializer'
import { PolicyAttributesEditor } from './policy-attributes-editor'
import { PolicyRulesEditor } from './policy-rules-editor'

interface PolicyEditFormProps {
  /** The policy to edit (null for create mode) */
  policy: PolicyDraft | null
  /** Callback when form is submitted */
  onSubmit: (data: UpdatePolicyRequest) => void
  /** Callback when cancel is clicked */
  onCancel: () => void
  /** Whether the form is submitting */
  isSubmitting?: boolean
  /** Optional class name */
  className?: string
  /** Force create mode even when policy is provided (for prefilled policies) */
  forceCreateMode?: boolean
}

interface FormErrors {
  name?: string
  description?: string
  policyId?: string
}

/**
 * PolicyEditForm Component
 * Provides structured editing for policy drafts
 */
export function PolicyEditForm({
  policy,
  onSubmit,
  onCancel,
  isSubmitting = false,
  className,
  forceCreateMode = false,
}: PolicyEditFormProps) {
  // Determine if we're in create mode
  const isCreateMode = forceCreateMode || policy === null

  // Parse existing XACML content or create empty structure
  const initialParsedPolicy = useMemo<ParsedXacmlPolicy>(() => {
    if (policy?.xacmlPolicy) {
      const parsed = parseXacmlPolicy(policy.xacmlPolicy)
      if (parsed) {
        return parsed
      }
    }
    return createEmptyPolicy()
  }, [policy?.xacmlPolicy])

  // Form state
  const [name, setName] = useState(policy?.name ?? '')
  const [description, setDescription] = useState(policy?.description ?? '')
  const [policyId, setPolicyId] = useState(initialParsedPolicy.policyId)
  const [target, setTarget] = useState<ParsedXacmlAttributes>(initialParsedPolicy.target)
  const [rules, setRules] = useState(initialParsedPolicy.rules)
  const [showAdvancedPreview, setShowAdvancedPreview] = useState(false)

  // Validation errors
  const [errors, setErrors] = useState<FormErrors>({})

  // Track original values for dirty detection
  const originalValues = useMemo(
    () => ({
      name: policy?.name ?? '',
      description: policy?.description ?? '',
      xacmlJson: policy?.xacmlPolicy ? JSON.stringify(policy.xacmlPolicy) : '',
    }),
    [policy?.name, policy?.description, policy?.xacmlPolicy]
  )

  // Build current XACML from structured data
  const currentXacml = useMemo(() => {
    const parsedPolicy: ParsedXacmlPolicy = {
      policyId,
      version: initialParsedPolicy.version,
      description: initialParsedPolicy.description,
      ruleCombiningAlgorithm: initialParsedPolicy.ruleCombiningAlgorithm,
      target,
      rules,
    }
    return serializeToXacml(parsedPolicy)
  }, [policyId, target, rules, initialParsedPolicy])

  // Compute dirty state
  const isDirty = useMemo(() => {
    const currentXacmlJson = JSON.stringify(currentXacml)
    return (
      name !== originalValues.name ||
      description !== originalValues.description ||
      currentXacmlJson !== originalValues.xacmlJson
    )
  }, [name, description, currentXacml, originalValues])

  // Handle name change
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }))
    }
  }, [errors.name])

  // Handle description change
  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(e.target.value)
      if (errors.description) {
        setErrors((prev) => ({ ...prev, description: undefined }))
      }
    },
    [errors.description]
  )

  // Handle policy ID change
  const handlePolicyIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPolicyId(e.target.value)
      if (errors.policyId) {
        setErrors((prev) => ({ ...prev, policyId: undefined }))
      }
    },
    [errors.policyId]
  )

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {}

    if (!name.trim()) {
      newErrors.name = 'Policy name is required'
    } else if (name.length > 255) {
      newErrors.name = 'Policy name must be 255 characters or less'
    }

    if (description.length > 1000) {
      newErrors.description = 'Description must be 1000 characters or less'
    }

    if (!policyId.trim()) {
      newErrors.policyId = 'Policy ID is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [name, description, policyId])

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()

      if (!validateForm()) {
        return
      }

      // Build update request
      const updateData: UpdatePolicyRequest = {}

      // In create mode, always include all fields
      if (isCreateMode) {
        updateData.name = name.trim()
        updateData.description = description.trim()
        updateData.xacmlPolicy = currentXacml as unknown as Record<string, unknown>
      } else {
        // In edit mode, only include changed fields
        if (name !== originalValues.name) {
          updateData.name = name.trim()
        }

        if (description !== originalValues.description) {
          updateData.description = description.trim()
        }

        // Always include XACML if anything changed
        const currentXacmlJson = JSON.stringify(currentXacml)
        if (currentXacmlJson !== originalValues.xacmlJson) {
          updateData.xacmlPolicy = currentXacml as unknown as Record<string, unknown>
        }
      }

      onSubmit(updateData)
    },
    [name, description, currentXacml, originalValues, validateForm, onSubmit, isCreateMode]
  )

  // Format XACML for preview
  const xacmlPreview = useMemo(
    () => JSON.stringify(currentXacml, null, 2),
    [currentXacml]
  )

  return (
    <form onSubmit={handleSubmit} className={cn('space-y-6', className)}>
      {/* Policy Info Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Policy Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Name field */}
          <div className="space-y-2">
            <Label htmlFor="policy-name">
              Policy Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="policy-name"
              value={name}
              onChange={handleNameChange}
              placeholder="Enter policy name"
              disabled={isSubmitting}
              aria-invalid={!!errors.name}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          {/* Description field */}
          <div className="space-y-2">
            <Label htmlFor="policy-description">Description</Label>
            <Textarea
              id="policy-description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter policy description (optional)"
              disabled={isSubmitting}
              rows={2}
              aria-invalid={!!errors.description}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {description.length}/1000 characters
            </p>
          </div>

          {/* Policy ID field */}
          <div className="space-y-2">
            <Label htmlFor="policy-id">
              XACML Policy ID <span className="text-destructive">*</span>
            </Label>
            <Input
              id="policy-id"
              value={policyId}
              onChange={handlePolicyIdChange}
              placeholder="e.g., document-access-policy"
              disabled={isSubmitting}
              className="font-mono"
              aria-invalid={!!errors.policyId}
            />
            {errors.policyId && (
              <p className="text-sm text-destructive">{errors.policyId}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Unique identifier for this policy in the XACML system
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Policy Target Section */}
      <PolicyAttributesEditor
        attributes={target}
        onChange={setTarget}
      />

      {/* Policy Rules Section */}
      <PolicyRulesEditor
        rules={rules}
        onChange={setRules}
      />

      {/* Advanced: Raw XACML Preview (Collapsible) */}
      <Collapsible open={showAdvancedPreview} onOpenChange={setShowAdvancedPreview}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="w-full justify-between px-4 py-3 h-auto border rounded-lg hover:bg-muted/50"
          >
            <div className="flex items-center gap-2">
              <Code className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Advanced: Raw XACML Preview</span>
            </div>
            {showAdvancedPreview ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 rounded-lg border bg-muted/30 p-4">
            <p className="text-xs text-muted-foreground mb-2">
              This is a read-only preview of the XACML JSON that will be saved
            </p>
            <pre className="text-xs font-mono overflow-auto max-h-[400px] p-3 bg-background rounded border">
              {xacmlPreview}
            </pre>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Form actions */}
      <div className="flex items-center justify-between border-t pt-4">
        {/* Dirty state indicator */}
        <div>
          {isDirty && (
            <p className="text-xs text-muted-foreground">
              You have unsaved changes
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || (!isCreateMode && !isDirty)}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Saving...' : isCreateMode ? 'Create Policy' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default PolicyEditForm
